import { UIEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import {
  ClothingByCategoryDto,
  getClothingByCategoryApi,
  GetClothingByCategoryDto,
} from "../../apis/clothing/get-clothing-by-category.api";
import {
  ClothingThemeWithCategoriesDto,
  getClothingThemeApi,
} from "../../apis/clothing-theme/get-clothing-theme.api";
import { CategorySidebar } from "../../components/CategorySidebar/CategorySidebar";
import { ClothingCard } from "../../components/ClothingCard";
import {
  ALL_CLOTHING_TAG_ID,
  ClothingTagFilter,
  ClothingTagFilterOption,
} from "../../components/ClothingTagFilter/ClothingTagFilter";
import { StoreDropdown } from "../../components/StoreDropdown";
import { ThemeTabs } from "../../components/ThemeTabs/ThemeTabs";
import { useRequest } from "../../lib/request/use-request";
import * as styles from "./index.module.scss";

const CATEGORY_PARAM = "category";
const STORE_PARAM = "store";
const THEME_PARAM = "theme";
const ALL_THEME_ID = "all";
const ALL_CATEGORY_ID = "all";
const PAGE_SIZE = 20;

export function Clothing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const explicitCategoryId = searchParams.get(CATEGORY_PARAM);
  const activeStoreId = searchParams.get(STORE_PARAM) ?? undefined;

  const { data: themeData, requestHandler: fetchThemes } = useRequest<
    ClothingThemeWithCategoriesDto[]
  >({
    api: getClothingThemeApi,
    title: "获取服装主题",
  });

  const {
    requestHandler: fetchClothingList,
    isLoading: isClothingListLoading,
  } = useRequest<
    {
      total: number;
      entities: ClothingByCategoryDto[];
    },
    GetClothingByCategoryDto
  >({
    api: getClothingByCategoryApi,
    title: "获取服装列表",
  });

  const [clothingItems, setClothingItems] = useState<ClothingByCategoryDto[]>(
    [],
  );
  const [clothingTotal, setClothingTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);
  const [activeTagId, setActiveTagId] = useState(ALL_CLOTHING_TAG_ID);

  useEffect(() => {
    void fetchThemes();
  }, [fetchThemes]);

  // An explicit theme selection always wins. Only deep links from ClothingDetail
  // (a categoryId with no theme param yet) fall back to deriving the owning theme.
  const activeThemeId = useMemo(() => {
    const explicitThemeId = searchParams.get(THEME_PARAM);
    if (explicitThemeId) return explicitThemeId;

    if (explicitCategoryId) {
      const owningTheme = themeData?.find((theme) =>
        theme.categories.some((category) => category.id === explicitCategoryId),
      );
      if (owningTheme) return owningTheme.id;
    }

    return themeData?.[0]?.id ?? ALL_THEME_ID;
  }, [explicitCategoryId, themeData, searchParams]);

  const visibleCategories = useMemo(() => {
    return themeData?.find((theme) => theme.id === activeThemeId)?.categories;
  }, [themeData, activeThemeId]);

  // No explicit categoryId in the URL defaults to the first category of the
  // active theme, mirroring how activeThemeId defaults to the first theme.
  const activeCategoryId =
    explicitCategoryId ?? visibleCategories?.[0]?.id ?? ALL_CATEGORY_ID;

  const handleThemeChange = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(CATEGORY_PARAM);
          next.set(THEME_PARAM, id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Pin the currently active theme explicitly so picking a category never
  // shifts the top theme tab, even if that category also exists under another theme.
  const handleCategoryChange = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(CATEGORY_PARAM, id);
          next.set(THEME_PARAM, activeThemeId);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, activeThemeId],
  );

  const handleStoreChange = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(STORE_PARAM, id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Reset pagination during render (not in the effect below) when the
  // store/category pair changes, per React's "adjusting state on prop
  // change" pattern — avoids an extra setState-in-effect render.
  const clothingListKey = `${activeStoreId ?? ""}:${activeCategoryId}`;
  const [clothingListResetKey, setClothingListResetKey] =
    useState(clothingListKey);

  if (clothingListKey !== clothingListResetKey) {
    setClothingListResetKey(clothingListKey);
    setClothingItems([]);
    setClothingTotal(0);
    setPageIndex(1);
    setActiveTagId(ALL_CLOTHING_TAG_ID);
  }

  // activeCategoryId only falls back to ALL_CATEGORY_ID while themes/categories
  // are still loading (or a theme has none) — not a real categoryId to query by.
  useEffect(() => {
    if (!activeStoreId || activeCategoryId === ALL_CATEGORY_ID) return;

    void fetchClothingList({
      param: {
        storeId: activeStoreId,
        categoryId: activeCategoryId,
        pageIndex: 1,
        pageSize: PAGE_SIZE,
      },
      onSuccess: (result) => {
        setClothingItems(result.entities);
        setClothingTotal(result.total);
      },
    });
  }, [activeStoreId, activeCategoryId, fetchClothingList]);

  const availableClothingTags = useMemo(() => {
    const tagMap = new Map<string, ClothingTagFilterOption>();

    for (const clothing of clothingItems) {
      for (const clothingToTag of clothing.tags) {
        tagMap.set(clothingToTag.tag.id, clothingToTag.tag);
      }
    }

    return Array.from(tagMap.values());
  }, [clothingItems]);

  // Tag matching happens client-side against the already-fetched category
  // page; pagination below is unaffected and keeps loading full pages.
  const filteredClothingItems = useMemo(() => {
    if (activeTagId === ALL_CLOTHING_TAG_ID) return clothingItems;

    return clothingItems.filter((clothing) =>
      clothing.tags.some(
        (clothingToTag) => clothingToTag.tag.id === activeTagId,
      ),
    );
  }, [clothingItems, activeTagId]);

  const hasMoreClothing = clothingItems.length < clothingTotal;

  const handleLoadMoreClothing = useCallback(() => {
    if (
      isClothingListLoading ||
      !hasMoreClothing ||
      !activeStoreId ||
      activeCategoryId === ALL_CATEGORY_ID
    ) {
      return;
    }

    const nextPageIndex = pageIndex + 1;

    void fetchClothingList({
      param: {
        storeId: activeStoreId,
        categoryId: activeCategoryId,
        pageIndex: nextPageIndex,
        pageSize: PAGE_SIZE,
      },
      onSuccess: (result) => {
        setClothingItems((prev) => [...prev, ...result.entities]);
        setClothingTotal(result.total);
        setPageIndex(nextPageIndex);
      },
    });
  }, [
    isClothingListLoading,
    hasMoreClothing,
    activeStoreId,
    activeCategoryId,
    pageIndex,
    fetchClothingList,
  ]);

  const handleClothingListScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const target = event.currentTarget;
      const distanceToBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      if (distanceToBottom < 120) {
        handleLoadMoreClothing();
      }
    },
    [handleLoadMoreClothing],
  );

  return (
    <div className={styles.clothingPage}>
      <header className={styles.clothingPageHeader}>
        <StoreDropdown activeId={activeStoreId} onSelect={handleStoreChange} />
        <ThemeTabs
          themes={themeData}
          activeId={activeThemeId}
          onChange={handleThemeChange}
        />
      </header>

      <div className={styles.clothingPageBody}>
        <CategorySidebar
          categories={visibleCategories}
          activeId={activeCategoryId}
          onChange={handleCategoryChange}
        />

        <main
          className={styles.clothingPageList}
          onScroll={handleClothingListScroll}
        >
          {activeCategoryId === ALL_CATEGORY_ID ? (
            <div className={styles.clothingPageEmpty}>暂无分类</div>
          ) : (
            <>
              {availableClothingTags.length > 0 && (
                <ClothingTagFilter
                  tags={availableClothingTags}
                  activeId={activeTagId}
                  onChange={setActiveTagId}
                />
              )}

              {filteredClothingItems.length === 0 ? (
                <div className={styles.clothingPageEmpty}>
                  {isClothingListLoading ? "加载中..." : "该分类下暂无服装"}
                </div>
              ) : (
                <>
                  <div className={styles.clothingPageGrid}>
                    {filteredClothingItems.map((clothing) => (
                      <ClothingCard
                        key={clothing.id}
                        clothing={clothing}
                        storeId={activeStoreId}
                      />
                    ))}
                  </div>
                  <div className={styles.clothingPageLoadMore}>
                    {isClothingListLoading
                      ? "加载中..."
                      : hasMoreClothing
                        ? ""
                        : "没有更多了"}
                  </div>
                </>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
